<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.licenseList.modal.new.buttonText')"
      @click="licenseModalOpen = true"
    />
    <export
      url="/licenses/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.licenses')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>

  <qr-code-modal
    v-model:open="qrModalOpen"
    :license="qrLicense"
  />

  <license-modal v-model:open="licenseModalOpen" v-model:license="editedLicense" @saved="refetch" />

  <div>
    <div class="ml-12 flex flex-wrap gap-x-10 gap-y-10 pb-10 pt-5">
      <CompanyPayoutStatisticsTable
        :statistics="{
          total: filteredStatistics?.total ?? {},
          filtered: filteredStatistics?.filtered ?? {},
        }"
        :hide-selected="true"
        :loading="loadingStatistics"
        :admins-are-allowed="true"
      />
    </div>
  <data-table
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    class="mb-auto ml-12"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
    :actions="actions"
    :disable-frontend-sorting="true"
  />
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { ref, reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { collOptionsProvider, useProvider } from '@utils/table/hooks/provider';

import { useDelete } from '@hooks/api';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type { Company, HydraCollection, License } from '@/entities/Entities';
import Export from '@utils/toolbar/panes/Export.vue';
import { useQueryRef } from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';
import { format } from 'date-fns';
import { formatTimeDurationFromSeconds } from '@/utils/helpers';
import QrCodeModal from './QrCodeModal.vue';
import LicenseModal from './LicenseModal.vue';
import Action from '@utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg';
import { useConfirmPrompt } from '@/components/utils/prompt/injects';
import { useFetchAction } from '@/hooks/fetch';
import { useNotify } from '@/components/utils/notifications';
import { StatisticsItem } from '@/hooks/company-payout';
import CompanyPayoutStatisticsTable from '@/components/utils/CompanyPayoutStatisticsTable.vue';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});

const licenseModalOpen = ref(false);
const editedLicense = ref();

const qrModalOpen = ref(false);
const qrLicense = ref<License>();

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const [deleteLicense, { loading: deleting }] = useDelete();
const confirm = useConfirmPrompt();
const [exec] = useFetchAction();
const notify = useNotify();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.licenseList.actions.showQrCode'),
    action: (item: License) => {
        qrLicense.value = item;
        licenseModalOpen.value = false;
        qrModalOpen.value = true;
    },
  },
  {
    title: t('pages.licenseList.actions.triggerPayout'),
    action: async (item: License) => {
      if (!item.payoutSchedule) return;
      const answer = await confirm?.(t('pages.licenseList.modal.triggerPayoutConfirm'));
      if (!answer) return;
      try {
        await exec(
          `${item.payoutSchedule?.['@id']}/trigger_payout`,
          {
            method: 'POST',
            body: {},
          }
        );
        notify?.({
          variant: 'success',
          title: t('pages.licenseList.modal.triggerPayoutSuccess'),
        });
      } catch (e) {
        notify?.({
          variant: 'error',
          title: t('pages.licenseList.modal.triggerPayoutError'),
        });
      }
    },
  },
  {
    title: t('pages.licenseList.actions.editLicense'),
    action: (item: License) => {
      qrModalOpen.value = false;
      licenseModalOpen.value = true;
      editedLicense.value = { ...item };
    },
  },
  {
    title: t('pages.licenseList.actions.deleteLicense'),
    action: async (item: License) => {
      if (await deleteLicense(`/licenses/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        refetch();
      }
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const fetchCompanies = collOptionsProvider<Company>(
  '/companies',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value) => ({
    label: value.name,
    value: value.name,
  }),
);

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: true,
    group: t('pages.licenseList.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'name',
    sortKey: ['startDate', 'endDate'],
    title: t('licenses.name'),
    sortable: true,
    visible: true,
    group: t('pages.campaignList.fieldGroups.general'),
    computed: (license: License) =>
      `${license.company?.name} - ${license.startDate ? format(new Date(license.startDate), 'dd.MM.yyyy') : ''} - ${license.endDate ? format(new Date(license.endDate), 'dd.MM.yyyy') : ''}`,
    to: (license: License) => ({
      name: 'company-license-details',
      params: { companyId: license.company?.id },
      query: {
        license: JSON.stringify(license['@id']),
      },
    }),
  },
  {
    key: 'company.name',
    title: t('licenses.company'),
    group: t('pages.licenseList.fieldGroups.general'),
    visible: true,
    filter: {
      type: 'dropdown',
      group: t('pages.licenseList.filterGroups.general'),
      options: {
        options: fetchCompanies,
        label: t('licenses.company'),
      },
    },
  },
  {
    key: 'current',
    title: t('licenses.current'),
    group: t('pages.licenseList.fieldGroups.general'),
    sortable: true,
    visible: true,
    computed: (item: License) => {
      if(!item.startDate || !item.endDate) return '-';
      const startDate = (new Date(item.startDate)).getTime();
      const endDate = (new Date(item.endDate)).getTime();
      const now = (new Date()).getTime();
      return (startDate < now && now < endDate) ? t('yes') : t('no');
    },
    filter: {
      key: 'current',
      type: 'bool',
      group: t('pages.licenseList.filterGroups.general'),
      options: {
        label: t('licenses.current'),
      },
    },
  },
  {
    key: 'startDate',
    title: t('licenses.start'),
    group: t('pages.licenseList.fieldGroups.general'),
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.licenseList.filterGroups.general'),
      options: {
        label: t('licenses.start'),
      },
    },
  },
  {
    key: 'endDate',
    title: t('licenses.end'),
    group: t('pages.licenseList.fieldGroups.general'),
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.licenseList.filterGroups.general'),
      options: {
        label: t('licenses.end'),
      },
    },
  },
  {
    key: 'duration',
    title: t('licenses.duration'),
    group: t('pages.licenseList.fieldGroups.general'),
    visible: true,
    sortable: true,
    filter: {
      type: 'range',
      group: t('pages.licenseList.filterGroups.general'),
      options: {
        label: t('licenses.duration'),
      },
    },
  },
  {
    key: 'automaticRenewal',
    title: t('licenses.automaticRenewal'),
    group: t('pages.licenseList.fieldGroups.general'),
    visible: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.licenseList.filterGroups.general'),
      options: {
        label: t('licenses.automaticRenewal'),
      },
    },
  },
  {
    key: 'followupLicense.id',
    title: t('licenses.followupLicense'),
    group: t('pages.licenseList.fieldGroups.general'),
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.licenseList.filterGroups.general'),
      options: {
        label: t('licenses.followupLicense'),
      },
    },
  },
  {
    key: 'transferCompleted',
    title: t('licenses.transferCompleted'),
    group: t('pages.licenseList.fieldGroups.general'),
    visible: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.licenseList.filterGroups.general'),
      options: {
        label: t('licenses.transferCompleted'),
      },
    },
  },
  {
    key: 'payoutSchedule.nextPayout',
    title: t('licenses.nextPayout'),
    group: t('pages.licenseList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.licenseList.filterGroups.general'),
      options: {
        label: t('licenses.nextPayout'),
      },
    },
  },
  {
    key: 'driversNoBox',
    title: t('licenses.driversNoBox'),
    group: t('pages.licenseList.fieldGroups.general'),
    visible: true,
    computed: (license: License) => {
      const seats = license.licenseSeats?.filter((seat) => seat.type === 'noBox') ?? []
      return seats.map((seat) => seat.numberOfDrivers ?? 0).reduce((partial:number, x:number) => partial + x, 0).toString()
    },
    filter: {
      type: 'range',
      group: t('pages.licenseList.filterGroups.general'),
      options: {
        label: t('licenses.driversNoBox'),
      },
    },
  },
  {
    key: 'seatsNoBox',
    title: t('licenses.seatsNoBox'),
    group: t('pages.licenseList.fieldGroups.general'),
    visible: true,
    computed: (license: License) => {
      const seats = license.licenseSeats?.filter((seat) => seat.type === 'noBox') ?? []
      return seats.map((seat) => seat.seats ?? 0).reduce((partial:number, x:number) => partial + x, 0).toString()
    },
    filter: {
      type: 'range',
      group: t('pages.licenseList.filterGroups.general'),
      options: {
        label: t('licenses.seatsNoBox'),
      },
    },
  },
  {
    key: 'driversBox',
    title: t('licenses.driversBox'),
    group: t('pages.licenseList.fieldGroups.general'),
    visible: true,
    computed: (license: License) => {
      const seats = license.licenseSeats?.filter((seat) => seat.type === 'box') ?? []
      return seats.map((seat) => seat.numberOfDrivers ?? 0).reduce((partial:number, x:number) => partial + x, 0).toString()
    },
    filter: {
      type: 'range',
      group: t('pages.licenseList.filterGroups.general'),
      options: {
        label: t('licenses.driversBox'),
      },
    },
  },
  {
    key: 'seatsBox',
    title: t('licenses.seatsBox'),
    group: t('pages.licenseList.fieldGroups.general'),
    visible: true,
    computed: (license: License) => {
      const seats = license.licenseSeats?.filter((seat) => seat.type === 'box') ?? []
      return seats.map((seat) => seat.seats ?? 0).reduce((partial:number, x:number) => partial + x, 0).toString()
    },
    filter: {
      type: 'range',
      group: t('pages.licenseList.filterGroups.general'),
      options: {
        label: t('licenses.seatsBox'),
      },
    },
  },
  {
    key: 'trackedDistance',
    title: t('campaign.trackedDistance'),
    group: t('pages.licenseList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    unit: 'km',
    formatter: (v: number) => (v / 1000).toFixed(2) + ' km',
    filter: {
      type: 'range',
      group: t('pages.licenseList.filterGroups.statistics'),
      options: {
        icon: <span>km</span>,
        label: t('campaign.trackedDistance'),
        transformOut: (v: number) => v * 1000,
        transformIn: (v: number) => v / 1000,
      },
    },
  },
  {
    key: 'trackedTime',
    title: t('campaign.trackedTime'),
    group: t('pages.licenseList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    formatter: (v: number) => formatTimeDurationFromSeconds(v),
    filter: {
      type: 'duration',
      group: t('pages.licenseList.filterGroups.statistics'),
      options: {
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
        label: t('campaign.trackedTime'),
      },
    },
  },
  {
    key: 'kcal',
    title: t('driver.kcal'),
    group: t('pages.licenseList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    unit: 'kcal',
    formatter: (v: number) => (v ? v.toFixed(2) : 0) + ' kcal',
    filter: {
      type: 'range',
      group: t('pages.licenseList.filterGroups.statistics'),
      options: {
        icon: <span>kcal</span>,
        label: t('driver.kcal'),
      },
    },
  },
  {
    key: 'co2',
    title: t('driver.co2'),
    group: t('pages.licenseList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    unit: 'kg',
    formatter: (v: number) => (v ? v.toFixed(2) : 0) + ' kg',
    filter: {
      type: 'range',
      group: t('pages.licenseList.filterGroups.statistics'),
      options: {
        icon: <span>kg</span>,
        label: t('driver.co2'),
      },
    },
  },
  {
    key: 'numOfTours',
    title: t('campaign.numOfTours'),
    group: t('pages.licenseList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.licenseList.filterGroups.statistics'),
      options: {
        label: t('campaign.numOfTours'),
      },
    },
  },
  {
    key: 'numOfTopPhotos',
    title: t('campaign.numOfTopPhotos'),
    group: t('pages.licenseList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.licenseList.filterGroups.statistics'),
      options: {
        label: t('campaign.numOfTopPhotos'),
      },
    },
  },
  {
    key: 'avgSpeed',
    title: t('campaign.avgSpeed'),
    group: t('pages.licenseList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    unit: 'km/h',
    formatter: (v: number) => (v * 3.6).toFixed(2) + ' km/h',
    filter: {
      type: 'range',
      group: t('pages.licenseList.filterGroups.statistics'),
      options: {
        icon: <span>km/h</span>,
        label: t('campaign.avgSpeed'),
        transformOut: (v: number) => v / 3.6,
        transformIn: (v: number) => v * 3.6,
      },
    },
  },
  {
    key: 'advertisementEnabled',
    title: t('licenses.advertisementEnabled'),
    group: t('pages.licenseList.fieldGroups.general'),
    visible: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.licenseList.filterGroups.general'),
      options: {
        label: t('licenses.advertisementEnabled'),
      },
    },
  },
];

const visibleColumns = useTableFields(
  columns,
);

export interface Statistics {
  filtered: StatisticsItem;
  selected?: StatisticsItem;
  total: StatisticsItem;
}

const {
  data: filteredStatistics,
  loading: loadingStatistics,
  refetch: refetchStatistics,
} = useProvider<Statistics>('/licenses/payout-statistics', providerParams);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<License>>('/licenses', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  })
);

const loading = computed(() => loadingTable.value || deleting.value);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
.medium {
  width: 300px;
  height: auto;
}
</style>

